import BOTTOM_SHEETS from 'components/bottomsheet/registry';
import { BottomSheetOpenParams, BottomSheetOptions, BottomSheetParams, BottomSheetPayload, BottomSheetResult, BottomSheetStatus, CloseOptions } from 'types/bottomsheet';
import { animate, motionValue } from 'framer-motion';
import { create } from 'zustand';

const DEFAULT_OPTIONS: BottomSheetOptions = {
  fullScreen: false,
  maxHeight: '80%',
  background: undefined,
  blur: false,
  draggable: true,
  scrollable: true,
  confirmCancel: { confirm: false },
  containerStyle: {},
  hideBar: false,
};

const DEFAULT_CLOSE_OPTIONS: CloseOptions = {
  skipConfirm: true,
  data: null,
};

type BottomSheetState = {
  open: (id: string, payload?: BottomSheetParams) => Promise<BottomSheetResult>;
  close: (id: string, options?: CloseOptions) => void;
  sheets: Record<string, BottomSheetPayload>;
  // 바텀시트의 애니메이션과 오픈 상태 통합으로 open, isOpen -> status 로 통합, 중복 오픈을 막기 위함
  setStatus: (id: string, status: BottomSheetStatus) => void;
  updateOptions: (id: string, options: BottomSheetOptions) => void;
  updateData: (id: string, data: Record<string, any>) => void;
  resolves: Record<string, (value: BottomSheetResult) => void>;
};

const useBottomSheetStore = create<BottomSheetState>((set, get) => ({
  open: (id, payload) => {
    return new Promise((resolve) => {
      if (get().sheets[id]?.status === 'processing') {
        return resolve({ id, data: null, lifetime: 0, error: 'already-processing' });
      }

      const options = { ...DEFAULT_OPTIONS, ...BOTTOM_SHEETS[id]?.options, ...payload?.options };

      const openPrams: BottomSheetOpenParams = { y: motionValue(0), status: 'processing', openTime: Date.now() };

      set({
        sheets: { ...get().sheets, [id]: { ...payload, id, data: payload?.data ?? {}, options, ...openPrams } },
        resolves: { ...get().resolves, [id]: resolve },
      });
    });
  },
  close: (id, options = DEFAULT_CLOSE_OPTIONS) => {
    const { sheets, open, close, resolves } = get();
    const sheet = sheets[id];

    if (!sheet) return;

    if (options.animate !== false) {
      if (sheet.status === 'processing') return;
      const currentY = sheet.y.get();
      if (typeof currentY === 'string' && currentY === '0%') sheet.y.set(0);
      animate(sheet.y, window.innerHeight, {
        duration: 0.3,
        ease: 'easeInOut',
        onComplete: () => close(id, { ...options, animate: false }),
      });
      return;
    }

    const confirmCancel = sheet?.options.confirmCancel;

    if (confirmCancel?.confirm && !options.skipConfirm) {
      open('Common', {
        id: 'Common',
        data: {
          title: confirmCancel?.title ?? brsc.lucky_777_closepopup1,
          description: confirmCancel?.description ?? brsc.page_only_purchase,
        },
      }).then((result) => {
        if (result.data) {
          close(id, { data: options.data ?? sheet.data, skipConfirm: true });
        }
      });
      return;
    }

    const resolve = resolves[id];
    const remainingResolves = { ...resolves };
    delete remainingResolves[id];

    set((state) => ({
      sheets: { ...state.sheets, [id]: { ...sheet, open: false, status: 'idle' } },
      resolves: remainingResolves,
    }));

    resolve?.({ id, data: options.data, lifetime: Date.now() - sheet.openTime });
  },
  setStatus: (id, status) => set({ sheets: { ...get().sheets, [id]: { ...get().sheets[id], status } } }),
  sheets: {},
  updateOptions: (id, options) => {
    set({ sheets: { ...get().sheets, [id]: { ...get().sheets[id], options: { ...get().sheets[id].options, ...options } } } });
  },
  updateData: (id, data) => {
    set({ sheets: { ...get().sheets, [id]: { ...get().sheets[id], data: { ...get().sheets[id].data, ...data } } } });
  },
  resolves: {},
}));

export default useBottomSheetStore;
